import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card, CardHeader, CardContent, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, Trash2, Edit3 } from "lucide-react";
import IncentiveSlabs from "@/pages/IncentiveSlabs";

const emptyDoctor = { employee_id:"", name:"", email:"", role_tier:"assistant", doctor_type:"consultation", branch:"", specialization:"", joining_date:"", status:"active", contact:"", remarks:"", fixed_salary:0 };
const emptyBranch = { name:"", code:"", city:"", state:"", status:"active" };
const BRANCH_FIELDS = [["name","Branch Name"],["code","Branch Code"],["city","City"],["state","State"]];

export default function Master() {
  const { user } = useAuth();
  const [tab, setTab] = useState("doctors");
  const [doctors, setDoctors] = useState([]);
  const [branches, setBranches] = useState([]);
  const [targets, setTargets] = useState(null);
  const [openDoc, setOpenDoc] = useState(false);
  const [openBr, setOpenBr] = useState(false);
  const [docForm, setDocForm] = useState(emptyDoctor);
  const [brForm, setBrForm] = useState(emptyBranch);
  const [editingDoc, setEditingDoc] = useState(null);
  const [editingBr, setEditingBr] = useState(null);

  const load = () => {
    api.get("/doctors").then(r => setDoctors(r.data));
    api.get("/branches").then(r => setBranches(r.data));
    api.get("/targets").then(r => setTargets(r.data)).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const saveDoctor = async () => {
    try {
      if (editingDoc) await api.patch(`/doctors/${editingDoc}`, docForm);
      else await api.post("/doctors", docForm);
      toast.success("Doctor saved"); setOpenDoc(false); setDocForm(emptyDoctor); setEditingDoc(null); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
  };
  const saveBranch = async () => {
    try {
      if (editingBr) await api.patch(`/branches/${editingBr}`, brForm);
      else await api.post("/branches", brForm);
      toast.success(editingBr ? "Branch updated" : "Branch added");
      setOpenBr(false); setBrForm(emptyBranch); setEditingBr(null); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
  };
  const deactivateBranch = async (id) => {
    if (!window.confirm("Deactivate this branch? Historical doctor & log data will be preserved.")) return;
    try { await api.delete(`/branches/${id}`); toast.success("Branch deactivated"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Delete failed"); }
  };
  const saveTargets = async () => {
    try { await api.put("/targets", targets); toast.success("Targets saved"); }
    catch (e) { toast.error("Save failed"); }
  };
  const deactivate = async (id) => {
    if (!window.confirm("Deactivate this doctor? Historical data will be preserved.")) return;
    await api.delete(`/doctors/${id}`); toast.success("Doctor deactivated"); load();
  };

  return (
    <div className="space-y-6" data-testid="master-root">
      <div>
        <div className="text-xs uppercase tracking-[0.3em] text-muted-foreground">Settings</div>
        <h1 className="mt-2 text-3xl font-heading font-medium">Master</h1>
        <div className="gold-underline mt-3" />
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="bg-navy-800 border border-navy-700">
          <TabsTrigger value="doctors" data-testid="master-tab-doctors">Doctor Master</TabsTrigger>
          <TabsTrigger value="branches" data-testid="master-tab-branches">Branch Master</TabsTrigger>
          <TabsTrigger value="targets" data-testid="master-tab-targets">Target Master</TabsTrigger>
          <TabsTrigger value="incentive" data-testid="master-tab-incentive">Incentive Settings</TabsTrigger>
        </TabsList>

        <TabsContent value="doctors" className="space-y-4 mt-4">
          <div className="flex justify-end">
            <Dialog open={openDoc} onOpenChange={setOpenDoc}>
              <DialogTrigger asChild>
                <Button onClick={() => { setDocForm(emptyDoctor); setEditingDoc(null); }} className="gold-bg hover:opacity-90" data-testid="add-doctor-btn"><Plus className="h-4 w-4 mr-2" />Add Doctor</Button>
              </DialogTrigger>
              <DialogContent className="bg-navy-800 border-navy-700 max-w-2xl">
                <DialogHeader><DialogTitle className="font-heading">{editingDoc ? "Edit Doctor" : "Add Doctor"}</DialogTitle></DialogHeader>
                <div className="grid grid-cols-2 gap-4 py-2">
                  {[["employee_id","Employee ID","text"],["name","Full Name","text"],["email","Email","email"],["specialization","Specialization","text"],["joining_date","Joining Date","date"],["contact","Contact","text"]].map(([k,l,t]) => (
                    <div key={k}><Label className="text-xs">{l}</Label>
                      <Input type={t} value={docForm[k] || ""} onChange={e => setDocForm(f => ({ ...f, [k]: e.target.value }))} className="bg-navy-900 border-navy-700" data-testid={`dr-${k}`} /></div>
                  ))}
                  <div>
                    <Label className="text-xs">Doctor Type</Label>
                    <Select value={docForm.doctor_type} onValueChange={v => setDocForm(f => ({ ...f, doctor_type: v }))}>
                      <SelectTrigger className="bg-navy-900 border-navy-700" data-testid="dr-doctor_type"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="consultation">Consultation / Clinical</SelectItem>
                        <SelectItem value="procedure">Procedure / Surgery</SelectItem>
                        <SelectItem value="both">Both Consultation + Procedure</SelectItem>
                        <SelectItem value="other">Other</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-xs">Role Tier</Label>
                    <Select value={docForm.role_tier} onValueChange={v => setDocForm(f => ({ ...f, role_tier: v }))}>
                      <SelectTrigger className="bg-navy-900 border-navy-700" data-testid="dr-role_tier"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="assistant">Assistant Doctor</SelectItem>
                        <SelectItem value="branch_head">Branch Head</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-xs">Branch</Label>
                    <Select value={docForm.branch} onValueChange={v => setDocForm(f => ({ ...f, branch: v }))}>
                      <SelectTrigger className="bg-navy-900 border-navy-700" data-testid="dr-branch"><SelectValue placeholder="Pick branch" /></SelectTrigger>
                      <SelectContent>{branches.map(b => <SelectItem key={b.branch_id} value={b.name}>{b.name}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-xs">Status</Label>
                    <Select value={docForm.status} onValueChange={v => setDocForm(f => ({ ...f, status: v }))}>
                      <SelectTrigger className="bg-navy-900 border-navy-700" data-testid="dr-status"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="active">Active</SelectItem>
                        <SelectItem value="inactive">Inactive</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <DialogFooter><Button className="gold-bg hover:opacity-90" onClick={saveDoctor} data-testid="dr-save-btn">Save</Button></DialogFooter>
              </DialogContent>
            </Dialog>
          </div>
          <Card className="bg-navy-800 border-navy-700">
            <CardContent className="p-0 overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-navy-900/60 uppercase text-[10px] tracking-widest text-muted-foreground">
                  <tr><th className="text-left px-5 py-3">ID</th><th className="text-left px-5 py-3">Doctor</th><th className="text-left px-5 py-3">Type</th><th className="text-left px-5 py-3">Branch</th><th className="text-left px-5 py-3">Status</th><th className="text-right px-5 py-3">Actions</th></tr>
                </thead>
                <tbody>
                  {doctors.map(d => (
                    <tr key={d.doctor_id} className="border-t border-navy-700" data-testid={`doctor-row-${d.doctor_id}`}>
                      <td className="px-5 py-3 num">{d.employee_id}</td>
                      <td className="px-5 py-3"><div>{d.name}</div><div className="text-xs text-muted-foreground">{d.email}</div></td>
                      <td className="px-5 py-3 gold-text uppercase text-xs">{d.doctor_type || "consultation"}</td>
                      <td className="px-5 py-3">{d.branch}</td>
                      <td className="px-5 py-3"><Badge className={`${d.status === "active" ? "rag-green" : "rag-red"} border-0 uppercase text-[10px]`}>{d.status}</Badge></td>
                      <td className="px-5 py-3 text-right">
                        <button className="text-muted-foreground hover:gold-text mr-3" onClick={() => { setDocForm(d); setEditingDoc(d.doctor_id); setOpenDoc(true); }} data-testid={`edit-${d.doctor_id}`}><Edit3 className="h-4 w-4 inline" /></button>
                        {user?.role === "founder" && <button className="text-muted-foreground hover:text-rose_alert" onClick={() => deactivate(d.doctor_id)} data-testid={`del-${d.doctor_id}`}><Trash2 className="h-4 w-4 inline" /></button>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="branches" className="space-y-4 mt-4">
          <div className="flex justify-end">
            <Dialog open={openBr} onOpenChange={(o) => { setOpenBr(o); if (!o) { setEditingBr(null); setBrForm(emptyBranch); } }}>
              <DialogTrigger asChild><Button onClick={() => { setBrForm(emptyBranch); setEditingBr(null); }} className="gold-bg hover:opacity-90" data-testid="add-branch-btn"><Plus className="h-4 w-4 mr-2" />Add Branch</Button></DialogTrigger>
              <DialogContent className="bg-navy-800 border-navy-700" aria-describedby={undefined}>
                <DialogHeader><DialogTitle>{editingBr ? "Edit Branch" : "Add Branch"}</DialogTitle></DialogHeader>
                <div className="grid grid-cols-2 gap-3">
                  {BRANCH_FIELDS.map(([k,l]) => (
                    <div key={k}><Label className="text-xs">{l}</Label><Input value={brForm[k] || ""} onChange={e => setBrForm(f => ({ ...f, [k]: e.target.value }))} className="bg-navy-900 border-navy-700" data-testid={`br-${k}`} /></div>
                  ))}
                  <div>
                    <Label className="text-xs">Status</Label>
                    <Select value={brForm.status} onValueChange={v => setBrForm(f => ({ ...f, status: v }))}>
                      <SelectTrigger className="bg-navy-900 border-navy-700" data-testid="br-status"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="active">Active</SelectItem>
                        <SelectItem value="inactive">Inactive</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <DialogFooter><Button className="gold-bg hover:opacity-90" onClick={saveBranch} data-testid="br-save-btn">{editingBr ? "Update Branch" : "Save Branch"}</Button></DialogFooter>
              </DialogContent>
            </Dialog>
          </div>
          <Card className="bg-navy-800 border-navy-700">
            <CardContent className="p-0 overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-navy-900/60 uppercase text-[10px] tracking-widest text-muted-foreground">
                  <tr><th className="text-left px-5 py-3">Name</th><th className="text-left px-5 py-3">Code</th><th className="text-left px-5 py-3">City</th><th className="text-left px-5 py-3">State</th><th className="text-left px-5 py-3">Status</th><th className="text-right px-5 py-3">Actions</th></tr>
                </thead>
                <tbody>
                  {branches.map(b => (
                    <tr key={b.branch_id} className="border-t border-navy-700" data-testid={`branch-row-${b.branch_id}`}>
                      <td className="px-5 py-3">{b.name}</td>
                      <td className="px-5 py-3 text-muted-foreground">{b.code || "—"}</td>
                      <td className="px-5 py-3">{b.city || "—"}</td>
                      <td className="px-5 py-3">{b.state || "—"}</td>
                      <td className="px-5 py-3"><Badge className={`${(b.status || "active") === "active" ? "rag-green" : "rag-red"} border-0 uppercase text-[10px]`}>{b.status || "active"}</Badge></td>
                      <td className="px-5 py-3 text-right">
                        <button className="text-muted-foreground hover:gold-text mr-3" onClick={() => { setBrForm({ name:b.name, code:b.code||"", city:b.city||"", state:b.state||"", status:b.status||"active" }); setEditingBr(b.branch_id); setOpenBr(true); }} data-testid={`edit-branch-${b.branch_id}`}><Edit3 className="h-4 w-4 inline" /></button>
                        {user?.role === "founder" && <button className="text-muted-foreground hover:text-rose_alert" onClick={() => deactivateBranch(b.branch_id)} data-testid={`del-branch-${b.branch_id}`}><Trash2 className="h-4 w-4 inline" /></button>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="targets" className="space-y-4 mt-4">
          <Card className="bg-navy-800 border-navy-700">
            <CardHeader className="border-b border-navy-700"><CardTitle className="font-heading text-base">Monthly Targets (default across doctors)</CardTitle></CardHeader>
            <CardContent className="p-5 space-y-4">
              {[["ppt_target","Monthly PPT Target"],["shoots_target","Monthly Educational Shoot Target"],["videos_target","Monthly Informative Video Target"],["teaching_target","Monthly Teaching / Student Session Target"]].map(([k,l]) => (
                <div key={k} className="grid grid-cols-2 gap-4 items-center">
                  <div className="text-sm">{l}</div>
                  <Input type="number" value={targets?.[k] ?? ""} onChange={e => setTargets(t => ({ ...t, [k]: Number(e.target.value) }))} className="bg-navy-900 border-navy-700 num" data-testid={`target-${k}`} />
                </div>
              ))}
              <Button className="gold-bg hover:opacity-90" onClick={saveTargets} data-testid="save-targets-btn">Save Targets</Button>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="incentive" className="mt-4">
          <IncentiveSlabs />
        </TabsContent>
      </Tabs>
    </div>
  );
}
